import { isAfter, isBefore, parseISO } from 'date-fns'
import { BrandColors, Opacities, SystemColors } from '@collabhouse/shared'

import { CollaborationState } from '../graphql/generated'
import {
  type Collaboration,
  CollaborationNextAction,
  CollaborationProofState,
  CollaborationType,
  DateState,
  type Filters,
  type Term,
} from '../providers/CollaborationProvider.types'
import { getUrlWithParams, routes } from './routes'

export const getProofState = (term: Term): CollaborationProofState => {
  const posts = term?.posts

  if (posts) {
    const postsHaveAcceptedProof = posts.some((post) =>
      post.proofOfDeliveries?.some(({ status }) => status === 'ACCEPTED'),
    )
    if (postsHaveAcceptedProof) return CollaborationProofState.accepted

    const postsHaveOpenProof = posts.some((post) => post.proofOfDeliveries?.some(({ status }) => !status))
    if (postsHaveOpenProof) return CollaborationProofState.delivered
  }
  return CollaborationProofState.awaiting
}

export const getDateState = (start: string, end: string, date: Date): DateState => {
  const startDate = parseISO(start)

  const endDate = end ? parseISO(end) : startDate

  if (isAfter(date, endDate)) {
    return DateState.passed
  }
  if (isBefore(date, startDate)) {
    return DateState.upcoming
  }
  return DateState.ongoing
}

const getLatestTerm = (collab: Collaboration): Term | null =>
  collab.terms.length !== 0 && collab.terms.reduce((acc, term) => (term.version > acc.version ? term : acc))

const noNextActionStates = [
  CollaborationState.Error,
  CollaborationState.Canceled,
  CollaborationState.Closed,
  CollaborationState.Declined,
  CollaborationState.Accepted,
]

export const getCollaborationNextAction = (collaboration: Collaboration, groupId: string): CollaborationNextAction => {
  if (!collaboration || !collaboration.terms) {
    return CollaborationNextAction.error
  }

  const collaborationState = collaboration.state
  const term = getLatestTerm(collaboration)
  const isFixedFee = term.type === CollaborationType.fixedFee
  const collaborationType = isFixedFee ? CollaborationType.fixedFee : CollaborationType.royaltyShare

  if (
    noNextActionStates.includes(collaborationState) ||
    (collaborationState === CollaborationState.Ongoing && collaborationType === CollaborationType.royaltyShare)
  ) {
    return CollaborationNextAction.none
  }

  const createdBy = term.createdByGroup?.id

  if (collaborationState === CollaborationState.Proposed) {
    return groupId === createdBy ? CollaborationNextAction.awaitingReview : CollaborationNextAction.reviewCollab
  }

  const to = collaboration.to.id
  const from = collaboration.from.id
  if (collaborationState === CollaborationState.Ongoing && collaborationType === CollaborationType.fixedFee) {
    if (groupId === to) return CollaborationNextAction.deliverProof
    if (groupId === from) return CollaborationNextAction.awaitingProof
  }

  if (collaborationState === CollaborationState.DraftDelivered) {
    if (groupId === to) return CollaborationNextAction.awaitingFeedback
    if (groupId === from) return CollaborationNextAction.provideFeedback
  }

  if (collaborationState === CollaborationState.DraftAccepted) {
    if (groupId === to) return CollaborationNextAction.deliverProof
    if (groupId === from) return CollaborationNextAction.awaitingProof
  }

  if (collaborationState === CollaborationState.DraftFeedback) {
    if (groupId === to) return CollaborationNextAction.deliverProof
    if (groupId === from) return CollaborationNextAction.awaitingProof
  }

  if (collaborationState === CollaborationState.Paid) {
    if (groupId === to) return CollaborationNextAction.provideDraft
    if (groupId === from) return CollaborationNextAction.awaitingDraft
  }

  if (collaborationState === CollaborationState.PaymentRequired) {
    if (groupId === to) return CollaborationNextAction.awaitingPayment
    if (groupId === from) return CollaborationNextAction.paymentRequired
  }
}

export const getCollabStatus = (state: CollaborationState) => collaborationStateDisplay[state] ?? ''

export const collaborationStateDisplay = {
  [CollaborationState.Accepted]: 'Active',
  [CollaborationState.Ongoing]: 'Active',
  [CollaborationState.PaymentRequired]: 'Payment required',
  [CollaborationState.PaymentPending]: 'Payment pending',
  [CollaborationState.OngoingPayment]: 'Ongoing payment',
  [CollaborationState.UpcomingPayment]: 'Upcoming payment',
  [CollaborationState.Closed]: 'Done',
  [CollaborationState.DraftAccepted]: 'Active',
  [CollaborationState.DraftDelivered]: 'Active',
  [CollaborationState.DraftFeedback]: 'Active',
  [CollaborationState.Paid]: 'Active',
  [CollaborationState.Declined]: 'Declined',
  [CollaborationState.Canceled]: 'Cancelled',
  [CollaborationState.Proposed]: 'Proposal',
}

export const collaborationNextActionDisplay = {
  [CollaborationNextAction.reviewCollab]: 'Approval',
  [CollaborationNextAction.awaitingReview]: 'Awaiting Approval',
  [CollaborationNextAction.deliverProof]: 'Submit Proof of Delivery',
  [CollaborationNextAction.reviewProof]: 'Review Proof of Delivery',
  [CollaborationNextAction.awaitingProof]: 'Awaiting Proof of Delivery',
  [CollaborationNextAction.awaitingDraft]: 'Awaiting Draft',
  [CollaborationNextAction.provideDraft]: 'Provide Draft',
  [CollaborationNextAction.paymentRequired]: 'Payment',
  [CollaborationNextAction.awaitingPayment]: 'Awaiting payment',
  [CollaborationNextAction.paymentPending]: 'Payment pending',
  [CollaborationNextAction.awaitingFeedback]: 'Awaiting feedback',
  [CollaborationNextAction.provideFeedback]: 'Provide feedback',
  [CollaborationNextAction.none]: 'None',
}

export const getFilteredCollaborations = (
  collaborations: Collaboration[],
  activeFilters: Filters,
  groupId: string,
): Collaboration[] => {
  const statusMatchCollabs = activeFilters.status
    ? getCollaborationsFilteredByStatus(collaborations, activeFilters.status)
    : collaborations
  return activeFilters.nextAction
    ? statusMatchCollabs.filter(
        (collaboration) => activeFilters.nextAction === getCollaborationNextAction(collaboration, groupId),
      )
    : statusMatchCollabs
}

export const getCollaborationsFilteredByStatus = (
  collaborations: Collaboration[],
  statusFilter: Filters['status'],
): Collaboration[] => {
  if (Array.isArray(statusFilter)) {
    return statusFilter.flatMap((state) => collaborations.filter((collaboration) => state === collaboration.state))
  } else {
    return collaborations.filter((collaboration) => statusFilter === collaboration.state)
  }
}

export const closeStatusMessage = (err): string => {
  switch (err?.errors[0]?.errorType) {
    case 'CONFLICT':
      return 'The collaboration is already cancelled, accepted or declined.'
    case 'EXPIRED':
      return 'A newer version of this collaboration exists. Please refresh the page before accepting.'
    case 'NOT_FOUND':
      return 'The collaboration does not exist.'
    case 'VALIDATION_ERROR':
      return 'Please check your input and try again.'
    default:
      return 'Something went wrong. Please try again later.'
  }
}

interface NextActionStyling {
  color: string
  opacity: number
}

export const getCollaborationNextActionStyles = (nextAction: CollaborationNextAction): NextActionStyling => {
  switch (nextAction) {
    case CollaborationNextAction.reviewCollab:
    case CollaborationNextAction.deliverProof:
    case CollaborationNextAction.reviewProof:
    case CollaborationNextAction.paymentRequired:
    case CollaborationNextAction.provideDraft:
    case CollaborationNextAction.provideFeedback:
      return {
        color: SystemColors.info.toString(),
        opacity: Opacities.full,
      }
    case CollaborationNextAction.awaitingProof:
    case CollaborationNextAction.awaitingReview:
    case CollaborationNextAction.awaitingPayment:
    case CollaborationNextAction.awaitingDraft:
    case CollaborationNextAction.awaitingFeedback:
      return {
        color: BrandColors.white.toString(),
        opacity: Opacities.full,
      }
    case CollaborationNextAction.none:
    default:
      return {
        color: BrandColors.white.toString(),
        opacity: Opacities.twentyfour,
      }
  }
}

export const getCollaborationNextActionInfo = (
  nextAction: CollaborationNextAction,
  collaborationId: string,
  additionalId?: string,
): { link?: string; status: string } => {
  switch (nextAction) {
    case CollaborationNextAction.reviewCollab:
      return {
        link: getUrlWithParams(routes.collaboration.details.url, {
          collaborationId,
        }),
        status: collaborationNextActionDisplay[nextAction],
      }
    case CollaborationNextAction.deliverProof:
      return {
        link: getUrlWithParams(routes.collaboration.details.deliverProofOfDelivery.url, {
          collaborationId,
          collaborationPostId: additionalId,
        }),
        status: collaborationNextActionDisplay[nextAction],
      }
    case CollaborationNextAction.paymentRequired:
      return {
        link: getUrlWithParams(routes.collaboration.details.payment.url, {
          collaborationId,
        }),
        status: collaborationNextActionDisplay[nextAction],
      }
    case CollaborationNextAction.provideDraft:
      return {
        link: getUrlWithParams(routes.collaboration.details.deliverDraft.url, {
          collaborationId,
          collaborationPostId: additionalId,
        }),
        status: collaborationNextActionDisplay[nextAction],
      }
    case CollaborationNextAction.provideFeedback:
      return {
        link: getUrlWithParams(routes.collaboration.details.reviewDraft.url, {
          collaborationId,
          proofOfDeliveryId: additionalId,
        }),
        status: collaborationNextActionDisplay[nextAction],
      }
    case CollaborationNextAction.awaitingProof:
    case CollaborationNextAction.awaitingReview:
    case CollaborationNextAction.awaitingPayment:
    case CollaborationNextAction.awaitingDraft:
    case CollaborationNextAction.awaitingFeedback:
    case CollaborationNextAction.reviewProof:
    case CollaborationNextAction.paymentPending:
    case CollaborationNextAction.none:
    default:
      return {
        status: collaborationNextActionDisplay[nextAction],
      }
  }
}
